import React, { useMemo } from 'react';
import {
  HoverCardContent,
  HoverCardRoot,
  HoverCardTrigger,
} from '@/components/ui/HoverCard';
import { FormFieldHoverCard } from './FormFieldHoverCard';
import {
  Avatar,
  Badge,
  Button,
  DataCell,
  DataList,
  Flex,
  ListItem,
  ScrollArea,
  Separator,
  Text,
} from '@/components/ui';
import {
  IconCornerLeftUp,
  IconFolder,
  IconInfoCircle,
} from '@tabler/icons-react';
import {
  getFieldIcon,
  getFieldTypeLabel,
  getFormDisplayName,
  getFormIcon,
  getFormIconColor,
  getFormTypeLabel,
} from '@/utils/formUtils';
import {
  buildFormTree,
  type FormTreeNode,
} from '@/pages/data-import/components/utils/formTreeUtils';
import type { FormField, StudyForm } from '@/types/studyForms';

interface StudyFormHoverCardProps {
  form: StudyForm;
  children?: React.ReactNode; // Required when variant is 'hover'
  fieldCount?: number;
  allForms?: Map<number, StudyForm>;
  variant?: 'hover' | 'card'; // 'hover' renders as hover card, 'card' renders as static card
}

// Helper function to extract fields from json_schema and convert to FormField format
type JsonSchemaFieldItem = {
  dataPoint?: unknown;
  subForm?: unknown;
  input?: { type?: unknown; display?: unknown; format?: unknown; required?: unknown } | null;
  question?: { list?: unknown; required?: unknown; label?: unknown } | null;
};

const extractFields = (jsonSchema: unknown): FormField[] => {
  const schema =
    typeof jsonSchema === 'object' && jsonSchema !== null
      ? (jsonSchema as { fieldsArray?: unknown })
      : null;

  if (!schema?.fieldsArray || !Array.isArray(schema.fieldsArray)) {
    return [];
  }

  const isStringArray = (value: unknown): value is string[] =>
    Array.isArray(value) && value.every(v => typeof v === 'string');

  return (schema.fieldsArray as JsonSchemaFieldItem[])
    .filter(
      (fieldItem): fieldItem is JsonSchemaFieldItem & { dataPoint: string } =>
        typeof fieldItem.dataPoint === 'string' && !fieldItem.subForm
    )
    .map(fieldItem => {
      let fieldType: 'text' | 'number' | 'date' | 'time' | 'radio' | 'select' =
        'text';

      if (fieldItem.input && typeof fieldItem.input === 'object') {
        const inputType = fieldItem.input.type;

        if (inputType === 'text') {
          if (
            fieldItem.question?.list &&
            Array.isArray(fieldItem.question.list)
          ) {
            fieldType = 'select';
          } else {
            fieldType = 'text';
          }
        } else if (inputType === 'number') {
          fieldType = 'number';
        } else if (inputType === 'date') {
          fieldType = 'date';
        } else if (inputType === 'time') {
          fieldType = 'time';
        } else if (inputType === 'radio') {
          fieldType = 'radio';
        }
      }

      // Get allowed values from question.list or input.display
      const allowedValues =
        fieldItem.question?.list || fieldItem.input?.display || undefined;

      // Get required status from question.required or input.required
      const required =
        fieldItem.question?.required || fieldItem.input?.required || false;

      return {
        dataPoint: fieldItem.dataPoint,
        label:
          (typeof fieldItem.question?.label === 'string'
            ? fieldItem.question.label
            : undefined) || fieldItem.dataPoint,
        type: fieldType,
        format:
          typeof fieldItem.input?.format === 'string'
            ? fieldItem.input.format
            : undefined,
        required: Boolean(required),
        allowedValues: isStringArray(allowedValues) ? allowedValues : undefined,
      };
    });
};

// Helper function to find parent form
const findParentForm = (
  form: StudyForm,
  allForms: Map<number, StudyForm>
): StudyForm | null => {
  if (!allForms || allForms.size === 0) {
    return null;
  }

  // Search through all forms to find one that has this form as a subform
  for (const [, potentialParent] of allForms) {
    if (
      potentialParent.json_schema?.subFormArray &&
      Array.isArray(potentialParent.json_schema.subFormArray)
    ) {
      if (potentialParent.json_schema.subFormArray.includes(form.name)) {
        return potentialParent;
      }
    }
  }

  return null;
};

// Field display component - shows field information with info button trigger
const FieldDisplay: React.FC<{ field: FormField }> = ({ field }) => {
  const FieldIcon = getFieldIcon(field.type);

  return (
    <Flex
      align="center"
      gap="2"
      style={{
        padding: 'var(--space-1) var(--space-2)',
        borderRadius: 'var(--radius)',
        background: 'var(--gray-a2)',
        transition: 'all 0.2s ease',
      }}
    >
      <FieldIcon />
      <Text
        size="3xsmall"
        weight="medium"
        style={{
          fontFamily: 'var(--font-mono)',
          lineHeight: 1.2,
          flex: 1,
        }}
      >
        {field.dataPoint}
      </Text>
      <Badge color="info" variant="surface" size="xsmall">
        {getFieldTypeLabel(field.type)}
      </Badge>
      <FormFieldHoverCard field={field}>
        <Button
          variant="ghost"
          size="xsmall"
          isIconOnly
          style={{
            padding: '2px',
            minWidth: 'auto',
            width: '20px',
            height: '20px',
          }}
        >
          <IconInfoCircle size={12} color="var(--blue-9)" />
        </Button>
      </FormFieldHoverCard>
    </Flex>
  );
};

// Shared content component that can be used in both hover card and card variants
interface StudyFormCardContentProps {
  form: StudyForm;
  fieldCount?: number;
  allForms?: Map<number, StudyForm>;
  showAllFields?: boolean; // If true, show all fields instead of just first 10
}

export const StudyFormCardContent: React.FC<StudyFormCardContentProps> = ({
  form,
  fieldCount,
  allForms,
  showAllFields = false,
}) => {
  const formTypeLabel = getFormTypeLabel(form.type);
  const displayName = getFormDisplayName(form);

  // Determine if form has children (subforms) for better icon selection
  const hasChildren = useMemo(() => {
    if (!allForms || allForms.size === 0) return false;
    const formsArray = Array.from(allForms.values());
    const formTree = buildFormTree(formsArray);

    // Create a flat map for quick lookup
    const formMap = new Map<number, FormTreeNode>();
    const buildMap = (nodes: FormTreeNode[]) => {
      nodes.forEach(node => {
        formMap.set(node.id, node);
        if (node.children) buildMap(node.children);
      });
    };
    buildMap(formTree);

    const treeNode = formMap.get(form.id);
    return (treeNode?.children?.length ?? 0) > 0;
  }, [form.id, allForms]);

  // Get form icon based on type and whether it has children
  const FormIconComponent = getFormIcon(form.type, hasChildren);
  const formIconColor = getFormIconColor(form.type);

  // Extract fields from json_schema
  const fields = useMemo(
    () => extractFields(form.json_schema),
    [form.json_schema]
  );

  // Find parent form if exists
  const parentForm = useMemo(() => {
    if (!allForms || allForms.size === 0) {
      return null;
    }
    return findParentForm(form, allForms);
  }, [form, allForms]);

  // Calculate actual field count
  const actualFieldCount = fieldCount ?? fields.length;

  return (
    <Flex direction="column" gap="3" style={{ width: '100%' }}>
      {/* Header */}
      <Flex align="center" gap="2">
        <Avatar variant="soft" radius="large">
          <FormIconComponent size={24} style={{ color: formIconColor }} />
        </Avatar>
        <Flex direction="column" gap="1" style={{ flex: 1 }}>
          <Text size="2xsmall" weight="medium">
            {displayName}
          </Text>
          <Flex align="center" gap="2" wrap="wrap">
            {form.json_schema?.displayName &&
              form.json_schema.displayName !== form.name && (
                <Text
                  size="1"
                  color="gray"
                  style={{ fontFamily: 'var(--font-mono)' }}
                >
                  {form.name}
                </Text>
              )}

            <Badge color="info" variant="surface" size="xsmall">
              {formTypeLabel}
            </Badge>
          </Flex>
        </Flex>
      </Flex>

      {/* Parent Form */}
      {parentForm && (
        <>
          {/*<Separator />*/}
          <ListItem
            disableHoverEffect
            style={{
              padding: 'var(--space-1) var(--space-4)',
              background: 'var(--gray-a2)',
              borderRadius: 'var(--radius)',
              cursor: 'auto',
              userSelect: 'auto',
            }}
            startIcon={
              <IconCornerLeftUp
                size={16}
                color="var(--gray-9)"
                style={{ marginRight: 'var(--space-4)' }}
              />
            }
            primary={
              <Text size="3xsmall" weight="medium">
                Parent Form
              </Text>
            }
            secondary={
              <Flex align="center" gap="2">
                <IconFolder size={16} color="var(--blue-9)" />
                <Text size="2xsmall" weight="medium">
                  {parentForm.json_schema?.displayName || parentForm.name}
                </Text>
              </Flex>
            }
          ></ListItem>
        </>
      )}

      {form.description && (
        <>
          <Separator />
          <Text size="1" color="gray">
            {form.description}
          </Text>
        </>
      )}

      <Separator />

      {/* Form Details */}
      <DataList
        items={useMemo(() => {
          const items: Array<
            { label: string; value: React.ReactNode } | { type: 'separator' }
          > = [
            {
              label: 'Study Form ID',
              value: (
                <DataCell noWrapper type="id" value={form.id} size="small" />
              ),
            },
          ];

          if (actualFieldCount > 0) {
            items.push({
              label: 'Fields',
              value: (
                <DataCell
                  noWrapper
                  type="number"
                  value={actualFieldCount}
                  size="small"
                />
              ),
            });
          }

          if (
            form.json_schema?.subFormArray &&
            Array.isArray(form.json_schema.subFormArray) &&
            form.json_schema.subFormArray.length > 0
          ) {
            items.push({
              label: 'Subforms',
              value: (
                <DataCell
                  noWrapper
                  type="badge"
                  value={form.json_schema.subFormArray.length}
                  variant="soft"
                  color="info"
                  size="small"
                />
              ),
            });
          }

          // Add separator before dates
          items.push({ type: 'separator' });

          items.push({
            label: 'Created',
            value: (
              <DataCell
                noWrapper
                type="datetime"
                value={form.created_at}
                relative={false}
                format="MMM D, YYYY"
                size="small"
              />
            ),
          });

          items.push({
            label: 'Updated',
            value: (
              <DataCell
                noWrapper
                type="datetime"
                value={form.updated_at}
                relative={false}
                format="MMM D, YYYY"
                size="small"
              />
            ),
          });

          return items;
        }, [form, actualFieldCount])}
        size="medium"
        orientation="horizontal"
        alignValues="end"
      />

      {/* Fields Preview */}
      {fields.length > 0 && (
        <>
          <Separator />
          <Flex direction="column" gap="2">
            <Text size="3xsmall" weight="medium">
              Form Fields ({fields.length})
            </Text>
            <ScrollArea
              type="auto"
              style={{
                maxHeight: showAllFields ? 'none' : '200px',
                padding: '4px 0',
              }}
            >
              <Flex direction="column" gap="1" pr="2">
                {(showAllFields ? fields : fields.slice(0, 10)).map(
                  field => (
                    <FieldDisplay key={field.dataPoint} field={field} />
                  )
                )}
                {!showAllFields && fields.length > 10 && (
                  <Text size="1" color="gray" style={{ padding: '4px 8px' }}>
                    +{fields.length - 10} more fields
                  </Text>
                )}
              </Flex>
            </ScrollArea>
          </Flex>
        </>
      )}
    </Flex>
  );
};

export const StudyFormHoverCard: React.FC<StudyFormHoverCardProps> = ({
  form,
  children,
  fieldCount,
  allForms,
  variant = 'hover',
}) => {
  // If variant is 'card', render as static card without hover wrapper
  if (variant === 'card') {
    return (
      <StudyFormCardContent
        form={form}
        fieldCount={fieldCount}
        allForms={allForms}
        showAllFields={true}
      />
    );
  }

  // Default hover card variant
  return (
    <HoverCardRoot>
      {/* @ts-expect-error - asChild is supported by Radix UI but not in types */}
      <HoverCardTrigger asChild>{children}</HoverCardTrigger>
      <HoverCardContent size="2">
        <StudyFormCardContent
          form={form}
          fieldCount={fieldCount}
          allForms={allForms}
        />
      </HoverCardContent>
    </HoverCardRoot>
  );
};
